/**
 * Schema-aware validation of a {@link Rule} against a {@link Catalog}.
 *
 * This is what makes the schema load-bearing before the evaluator exists: it
 * walks a rule tracking which relation each bound variable ranges over, and
 * checks the things the coercions depend on —
 *   - quantifiers range over known relations;
 *   - bare row references are bound;
 *   - an `.attr` names a real column of its row's relation;
 *   - an applied reference is value-coercible (one non-key column), its argument
 *     count / keyword names match the relation's keys, and a composite-key row
 *     spread into it matches position-by-position by *referenced relation*.
 * Returns a list of human-readable messages; empty means the rule is well-formed.
 *
 * Each message also names the AST node it's about ({@link validateRuleDiagnostics}),
 * which is what lets the editor underline the offending word: validation itself
 * never sees the text, so it reports the node and leaves the lookup to whoever
 * holds the parse's spans.
 */

import { Catalog, RelationSchema, columnsOf, isValueCoercible, keyArity, foreignKeys } from './schema';
import { Diagnostic, messagesOf } from './diagnostics';
import { Rule, Expr, Pred, Quantifier } from './ruleAst';

type Scope = Map<string, RelationSchema>;

/**
 * The key positions an argument fills, as referenced-relation types (one entry
 * per position, `undefined` = not type-checked). A bare *composite*-key row
 * spreads into its key columns' referenced relations; anything else — a scalar
 * row, an `.attr`, a literal, arithmetic — occupies a single untyped slot.
 */
function argSlots(e: Expr, scope: Scope, catalog: Catalog): (string | undefined)[] {
  if (e.kind === 'ref') {
    const schema = scope.get(e.name);
    if (schema && keyArity(schema) > 1) {
      const fks = foreignKeys(schema, catalog);
      return schema.keys.map((k) => fks[k]);
    }
  }
  return [undefined];
}

/** Every problem with a rule, each carrying the node it's about. */
export function validateRuleDiagnostics(rule: Rule, catalog: Catalog): Diagnostic[] {
  const errs: Diagnostic[] = [];
  const scope: Scope = new Map();
  for (const q of rule.quantifiers) bindQuantifier(q, scope, catalog, errs);
  checkBody(rule, scope, catalog, errs);
  return errs;
}

/** {@link validateRuleDiagnostics} as plain messages. Empty means well-formed. */
export function validateRule(rule: Rule, catalog: Catalog): string[] {
  return messagesOf(validateRuleDiagnostics(rule, catalog));
}

/** Validate every rule, returning `{ ruleName: messages }` for any that fail. */
export function validateRules(rules: Rule[], catalog: Catalog): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const rule of rules) {
    const errs = validateRule(rule, catalog);
    if (errs.length) out[rule.name] = errs;
  }
  return out;
}

function bindQuantifier(q: Quantifier, scope: Scope, catalog: Catalog, errs: Diagnostic[]): void {
  const schema = catalog[q.relation];
  if (!schema) {
    errs.push({ message: `unknown relation '${q.relation}' in quantifier '${q.var}'`, node: q });
  } else {
    scope.set(q.var, schema);
  }
  if (q.where) checkPred(q.where, scope, catalog, errs);
}

function checkBody(rule: Rule, scope: Scope, catalog: Catalog, errs: Diagnostic[]): void {
  const body = rule.body;
  if (body.kind === 'define') {
    // A definition's value is data: it's computed before the solver runs, so it
    // can't read a variable the solver hasn't assigned yet.
    checkExpr(body.expr, scope, catalog, errs);
    forbidDecisions(body.expr, catalog, errs);
  } else if (body.kind === 'range') {
    checkExpr(body.lower, scope, catalog, errs);
    checkExpr(body.expr, scope, catalog, errs);
    checkExpr(body.upper, scope, catalog, errs);
  } else {
    checkExpr(body.left, scope, catalog, errs);
    checkExpr(body.right, scope, catalog, errs);
  }
}

/** Report any decision variable read inside a derived relation's definition. */
function forbidDecisions(e: Expr, catalog: Catalog, errs: Diagnostic[]): void {
  switch (e.kind) {
    case 'apply':
      if (catalog[e.relation]?.kind === 'decision') {
        errs.push({
        message: `'${e.relation}' is a decision variable; a derived relation is computed before the solve, so it can't read one`,
        node: e,
      });
      }
      e.args.forEach((a) => forbidDecisions(a.value, catalog, errs));
      return;
    case 'call':
      e.args.forEach((a) => forbidDecisions(a, catalog, errs));
      return;
    case 'neg':
      return forbidDecisions(e.operand, catalog, errs);
    case 'binary':
      forbidDecisions(e.left, catalog, errs);
      return forbidDecisions(e.right, catalog, errs);
    case 'sum':
      return forbidDecisions(e.body, catalog, errs);
    case 'cond':
      // The test can only compare bound rows, so there's nothing to forbid in
      // it; the branches are ordinary values.
      forbidDecisions(e.then, catalog, errs);
      return forbidDecisions(e.otherwise, catalog, errs);
    default:
      return;
  }
}

function checkPred(p: Pred, scope: Scope, catalog: Catalog, errs: Diagnostic[]): void {
  if (p.kind === 'and') {
    checkPred(p.left, scope, catalog, errs);
    checkPred(p.right, scope, catalog, errs);
  } else {
    checkExpr(p.left, scope, catalog, errs);
    checkExpr(p.right, scope, catalog, errs);
  }
}

function checkExpr(e: Expr, scope: Scope, catalog: Catalog, errs: Diagnostic[]): void {
  switch (e.kind) {
    case 'num':
    case 'str':
      return;
    case 'ref': {
      // A bare row denotes its key tuple (single key unwraps to a scalar,
      // composite keys spread where indexed), so any bound variable is fine.
      if (!scope.has(e.name)) errs.push({ message: `unbound variable '${e.name}'`, node: e });
      return;
    }
    case 'attr': {
      const schema = scope.get(e.obj);
      if (!schema) errs.push({ message: `unbound variable '${e.obj}' in '${e.obj}.${e.col}'`, node: e });
      else if (!columnsOf(schema).includes(e.col)) {
        errs.push({
          message: `relation '${schema.name}' has no column '${e.col}' (in '${e.obj}.${e.col}')`,
          node: e,
        });
      }
      return;
    }
    case 'apply': {
      const schema = catalog[e.relation];
      if (!schema) {
        errs.push({ message: `unknown relation '${e.relation}'`, node: e });
        return;
      }
      if (!isValueCoercible(schema)) {
        errs.push({
          message: `relation '${e.relation}' has ${schema.values.length} non-key columns; an applied reference isn't value-coercible (use '<var>.<column>')`,
          node: e,
        });
      }
      // Walk the argument list as a flat sequence of key positions. Positional
      // args spread by key arity (a composite-key row fills several positions and
      // is type-checked against the target's key references); keyword args fill
      // one named key each.
      const wantFks = foreignKeys(schema, catalog);
      const wantTypes = schema.keys.map((k) => wantFks[k]);
      let slot = 0;
      for (const arg of e.args) {
        if (arg.name) {
          if (!schema.keys.includes(arg.name)) {
            errs.push({ message: `relation '${e.relation}' has no key '${arg.name}'`, node: e });
          }
          slot += 1;
        } else {
          const got = argSlots(arg.value, scope, catalog);
          got.forEach((gotType, i) => {
            const wantType = wantTypes[slot + i];
            if (gotType && wantType && gotType !== wantType) {
              errs.push({
                message: `relation '${e.relation}' key '${schema.keys[slot + i]}' expects a ${wantType} but got a ${gotType}`,
                node: arg.value,
              });
            }
          });
          slot += got.length;
        }
        checkExpr(arg.value, scope, catalog, errs);
      }
      if (slot !== schema.keys.length) {
        errs.push({ message: `relation '${e.relation}' expects ${schema.keys.length} key value(s), got ${slot}`, node: e });
      }
      return;
    }
    case 'call':
      e.args.forEach((a) => checkExpr(a, scope, catalog, errs));
      return;
    case 'neg':
      checkExpr(e.operand, scope, catalog, errs);
      return;
    case 'binary':
      checkExpr(e.left, scope, catalog, errs);
      checkExpr(e.right, scope, catalog, errs);
      return;
    case 'sum': {
      // The sum's quantifiers extend the scope for its body (and can join on
      // outer variables in their `where`).
      const child: Scope = new Map(scope);
      for (const q of e.over) bindQuantifier(q, child, catalog, errs);
      checkExpr(e.body, child, catalog, errs);
      return;
    }
    case 'cond':
      checkPred(e.when, scope, catalog, errs);
      checkExpr(e.then, scope, catalog, errs);
      checkExpr(e.otherwise, scope, catalog, errs);
      return;
  }
}
